import { type Ref, useId, useState } from 'react';

import { clsx } from 'clsx';

import {
  AlertInline,
  Autosuggest,
  Button,
  Chip,
  compareBySearchMatch,
  Label,
} from '@storebrand-digital/pretzel';
import { LockIcon } from '@storebrand-digital/pretzel/icons';

export interface ChipPickerOption {
  value: string;
  label: string;
}

export interface ChipPickerItem extends ChipPickerOption {
  locked?: boolean;
}

export interface ChipPickerTexts {
  legend: string;
  description?: string;
  empty?: string;
  add: string;
  inputLabel: string;
  noItemFound: string;
  showMore: string;
}

export interface ChipPickerProps {
  name: string;
  options: ChipPickerOption[];
  value: ChipPickerItem[];
  max: number;
  texts: ChipPickerTexts;
  onAdd: (option: ChipPickerOption) => void;
  onRemove: (value: string) => void;
  onInputChange?: (text: string) => void;
  error?: string;
  inputError?: string;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
}

export function ChipPicker({
  name,
  options,
  value,
  max,
  texts,
  onAdd,
  onRemove,
  onInputChange,
  error,
  inputError,
  inputRef,
  className,
}: ChipPickerProps) {
  const errorId = useId();
  const [showInput, setShowInput] = useState(value.length === 0);
  const [inputValue, setInputValue] = useState('');

  const groupError = showInput ? undefined : error;
  const fieldError = inputError ?? error;

  function changeInput(text: string) {
    setInputValue(text);
    onInputChange?.(text);
  }

  return (
    <fieldset
      className={clsx('border-none m-0 p-0 min-w-0', className)}
      aria-describedby={groupError ? errorId : undefined}
    >
      <Label as="legend" className="mb-body-near" heading={texts.legend} />
      {texts.description && (
        <p className="mb-heading-near text-secondary font-body-small">
          {texts.description}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-08">
        {value.length === 0 ? (
          texts.empty
        ) : (
          <ul
            data-hj-suppress
            className="flex flex-wrap items-center gap-08 list-none pl-0 m-0"
          >
            {value.map((item) => (
              <li key={item.value}>
                <Chip
                  value={item.value}
                  label={item.label}
                  checked
                  variant="input"
                  onValueChange={() => {
                    if (item.locked) {
                      return;
                    }
                    onRemove(item.value);
                    if (value.length === 1) {
                      setShowInput(true);
                    }
                  }}
                  labelProps={{
                    className: clsx(
                      item.locked &&
                        'pointer-events-none cursor-not-allowed [&_.iconRight]:hidden',
                    ),
                  }}
                  aria-disabled={item.locked ?? false}
                  iconLeft={item.locked ? LockIcon : undefined}
                />
              </li>
            ))}
          </ul>
        )}
        {!showInput && value.length < max && (
          <Button
            type="button"
            variant="subtle"
            size="medium"
            onClick={() => setShowInput(true)}
          >
            {texts.add}
          </Button>
        )}
      </div>
      <div id={errorId} role="alert">
        {groupError && (
          <AlertInline heading={groupError} variant="error" className="mt-08" />
        )}
      </div>
      {showInput && (
        <Autosuggest
          ref={inputRef}
          data-hj-suppress
          className="pt-display-near"
          name={name}
          label={texts.inputLabel}
          fluid
          value={inputValue}
          data={options.filter(
            (option) => !value.some((item) => item.value === option.value),
          )}
          filter={(item, search) =>
            item.label.toLowerCase().includes(search.toLowerCase())
          }
          compare={compareBySearchMatch}
          noItemFoundText={texts.noItemFound}
          showMoreConfig={{ buttonText: texts.showMore }}
          onChange={(event) => changeInput(event.target.value)}
          onItemSelect={(item) => {
            if (item) {
              onAdd({ value: item.value, label: item.label });
              changeInput('');
              setShowInput(false);
            }
          }}
          alert={
            fieldError
              ? { type: 'error', message: fieldError, role: 'alert' }
              : undefined
          }
        />
      )}
    </fieldset>
  );
}
